import { useEffect, useRef, useState } from 'react';
import './MailtoForm.css';

const INITIAL = {
  name: '',
  company: '',
  role: '',
  location: '',
  operationType: '',
  fleetSize: '',
  message: '',
};


export default function MailtoForm({ open, onClose }) {
  const [fields, setFields] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const firstInputRef = useRef(null);
  const overlayRef = useRef(null);

  // Focus first input when modal opens
  useEffect(() => {
    if (open) {
      setSent(false);
      setErrors({});
      const t = setTimeout(() => firstInputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!fields.name.trim())          next.name = 'Your name is required.';
    if (!fields.company.trim())       next.company = 'Company or organization name is required.';
    if (!fields.location.trim())      next.location = 'City and state are required.';
    if (!fields.operationType)        next.operationType = 'Please select your operation type.';
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const body = buildEmailBody(fields);
    const subject = encodeURIComponent('TaxiOps Future Availability Inquiry');
    const encodedBody = encodeURIComponent(body);

    window.location.href = `mailto:info@oldalexhub.com?subject=${subject}&body=${encodedBody}`;
    setSent(true);
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  if (!open) return null;

  return (
    <div
      className="mf-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mf-title"
    >
      <div className="mf-modal">
        {/* Header */}
        <div className="mf-header">
          <div>
            <span className="mf-badge">
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                <path d="M5.5 1l1 2.8 2.8.2-2.1 1.8.7 2.8-2.4-1.5L1.1 8.6l.7-2.8L0 4l2.8-.2L5.5 1z"
                      stroke="currentColor" strokeWidth="1" strokeLinejoin="round"/>
              </svg>
              Product Availability
            </span>
            <h2 id="mf-title" className="mf-title">Register Future Interest</h2>
            <p className="mf-subtitle">
              Complete this form and your email client will open with a pre-filled
              message to Old Alex Hub about TaxiOps availability.
            </p>
          </div>
          <button className="mf-close" aria-label="Close form" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M13.5 4.5l-9 9M4.5 4.5l9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {sent ? (
          <div className="mf-success">
            <div className="mf-success-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="12" stroke="#10b981" strokeWidth="1.5"/>
                <path d="M9 14l3.5 3.5 6.5-7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Email Client Opened</h3>
            <p>
              Your email application should have opened with your inquiry pre-filled.
              Review and send the message to complete your interest registration.
            </p>
            <p className="mf-success-email">info@oldalexhub.com</p>
            <button className="btn btn--outline mf-success-close" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form className="mf-form" onSubmit={handleSubmit} noValidate>
            <div className="mf-grid">
              {/* Name */}
              <div className={`mf-field ${errors.name ? 'mf-field--error' : ''}`}>
                <label className="mf-label" htmlFor="mf-name">
                  Full Name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="mf-name"
                  name="name"
                  type="text"
                  className="mf-input"
                  placeholder="Your full name"
                  value={fields.name}
                  onChange={handleChange}
                  ref={firstInputRef}
                  autoComplete="name"
                />
                {errors.name && <p className="mf-error">{errors.name}</p>}
              </div>

              {/* Company */}
              <div className={`mf-field ${errors.company ? 'mf-field--error' : ''}`}>
                <label className="mf-label" htmlFor="mf-company">
                  Company / Organization <span aria-hidden="true">*</span>
                </label>
                <input
                  id="mf-company"
                  name="company"
                  type="text"
                  className="mf-input"
                  placeholder="Transportation company name"
                  value={fields.company}
                  onChange={handleChange}
                  autoComplete="organization"
                />
                {errors.company && <p className="mf-error">{errors.company}</p>}
              </div>

              {/* Role */}
              <div className="mf-field">
                <label className="mf-label" htmlFor="mf-role">
                  Role / Title <span className="mf-optional">(optional)</span>
                </label>
                <input
                  id="mf-role"
                  name="role"
                  type="text"
                  className="mf-input"
                  placeholder="e.g. Operations Manager, Owner"
                  value={fields.role}
                  onChange={handleChange}
                  autoComplete="organization-title"
                />
              </div>

              {/* Location */}
              <div className={`mf-field ${errors.location ? 'mf-field--error' : ''}`}>
                <label className="mf-label" htmlFor="mf-location">
                  City &amp; State <span aria-hidden="true">*</span>
                </label>
                <input
                  id="mf-location"
                  name="location"
                  type="text"
                  className="mf-input"
                  placeholder="e.g. Orlando, FL"
                  value={fields.location}
                  onChange={handleChange}
                />
                {errors.location && <p className="mf-error">{errors.location}</p>}
              </div>

              {/* Operation Type */}
              <div className={`mf-field ${errors.operationType ? 'mf-field--error' : ''}`}>
                <label className="mf-label" htmlFor="mf-operationType">
                  Operation Type <span aria-hidden="true">*</span>
                </label>
                <select
                  id="mf-operationType"
                  name="operationType"
                  className="mf-select"
                  value={fields.operationType}
                  onChange={handleChange}
                >
                  <option value="">Select operation type…</option>
                  <option value="Taxi">Taxi</option>
                  <option value="NEMT (Non-Emergency Medical Transportation)">NEMT</option>
                  <option value="Taxi and NEMT">Taxi &amp; NEMT</option>
                  <option value="Other transportation">Other</option>
                </select>
                {errors.operationType && <p className="mf-error">{errors.operationType}</p>}
              </div>

              {/* Fleet Size */}
              <div className="mf-field">
                <label className="mf-label" htmlFor="mf-fleetSize">
                  Fleet Size <span className="mf-optional">(optional)</span>
                </label>
                <select
                  id="mf-fleetSize"
                  name="fleetSize"
                  className="mf-select"
                  value={fields.fleetSize}
                  onChange={handleChange}
                >
                  <option value="">Select fleet size…</option>
                  <option value="1–5 vehicles">1–5 vehicles</option>
                  <option value="6–15 vehicles">6–15 vehicles</option>
                  <option value="16–50 vehicles">16–50 vehicles</option>
                  <option value="51–100 vehicles">51–100 vehicles</option>
                  <option value="100+ vehicles">100+ vehicles</option>
                </select>
              </div>
            </div>

            {/* Message */}
            <div className="mf-field mf-field--full">
              <label className="mf-label" htmlFor="mf-message">
                Message <span className="mf-optional">(optional)</span>
              </label>
              <textarea
                id="mf-message"
                name="message"
                className="mf-textarea"
                placeholder="Tell us about your operation or what you're looking for…"
                rows={4}
                value={fields.message}
                onChange={handleChange}
              />
            </div>

            {/* Disclaimer */}
            <p className="mf-disclaimer">
              Submitting this form will open your email application with your details
              pre-filled. Review and send the email to complete your inquiry.
              TaxiOps is not accepting Phase One enrollments — this registers your
              interest for future phases only.
            </p>

            {/* Actions */}
            <div className="mf-actions">
              <button type="button" className="btn btn--outline mf-cancel" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary mf-submit">
                Open Email Client
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                  <path d="M2.5 7.5h10M9 4l3.5 3.5L9 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function buildEmailBody(f) {
  const line = (label, value) => value ? `${label}: ${value}\n` : '';
  return [
    'TaxiOps — Future Availability Inquiry',
    '======================================\n',
    line('Name',           f.name),
    line('Company',        f.company),
    line('Role / Title',   f.role),
    line('Location',       f.location),
    line('Operation Type', f.operationType),
    line('Fleet Size',     f.fleetSize),
    f.message ? `\nMessage:\n${f.message}\n` : '',
    '\n---',
    'Submitted via the TaxiOps website interest form.',
    'TaxiOps is an official business venture of Old Alex Hub.',
  ].join('\n');
}
